import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { Link } from 'react-router-dom';
import { 
  BookMarked, Gem, TrendingUp, Flame, Trophy,
  Clock, MapPin, Sunrise, Sun, CloudSun, Sunset, Moon,
  Check, CheckCircle2, Compass, BookOpen, Headphones, Heart, Sparkles as SparklesIcon, Quote, Settings, Trash2, X
} from 'lucide-react';
import { 
  getPrayersToday, getDhikrToday, getDailySummary, getDailyContent,
  getPrayerTimes, getUserPrayerSettings, updatePrayer, logBereketActivity, getBereketActivities,
  getHadithOfTheDay, getUserNotificationSettings, startNotificationScheduler,
  getTodayBereketActivityLogs, deleteBereketActivityLog,
  getTodayBarakahPlan, saveTodayBarakahPlan, getLocalDateString,
  createDefaultTodayPlan, createEmptyTodayPlanPriority, hasPriorityTimeRange, hasTodayPlanContent,
  deletePlannerEvent,
} from '../lib/api';
import { getPriorityWarningMap, validatePriorityRanges } from '../lib/plannerPriorityValidation';
import DailyStreakStats from '../components/DailyStreakStats';
import { toast } from 'sonner';
import { usePlannerPriorityTimeNormalization } from '../hooks/usePlannerPriorityTimeNormalization';

const LEVEL_TRANSLATIONS = {
  'Day of Spiritual Breakthrough': 'Dan duhovnog proboja',
  'Day of Barakah': 'Dan bereketa',
  'Day of Growth': 'Dan rasta',
  'Day of Awareness': 'Dan svjesnosti',
  'Starting the Journey': 'Početak puta',
};

const PRAYER_ICONS = {
  fajr: Sunrise,
  sunrise: Sun,
  dhuhr: CloudSun,
  asr: Sun,
  maghrib: Sunset,
  isha: Moon,
};

const PRAYER_NAMES = {
  en: { fajr: 'Fajr', sunrise: 'Sunrise', dhuhr: 'Dhuhr', asr: 'Asr', maghrib: 'Maghrib', isha: 'Isha' },
  hr: { fajr: 'Sabah', sunrise: 'Izlazak sunca', dhuhr: 'Podne', asr: 'Ikindija', maghrib: 'Akšam', isha: 'Jacija' },
};

const ensureTodayPlanHasGoal = (plan, date = getLocalDateString()) => ((plan?.priorities?.length || 0) > 0 ? plan : createDefaultTodayPlan(date));

export default function Dashboard({ user }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [prayers, setPrayers] = useState(null);
  const [dailyContent, setDailyContent] = useState(null);
  const [dhikrCount, setDhikrCount] = useState(null);
  const [dailySummary, setDailySummary] = useState(null);
  const [showStatsModal, setShowStatsModal] = useState(false);

  // Vaktija state
  const [prayerData, setPrayerData] = useState(null);
  const [prayerSettings, setPrayerSettings] = useState(null);
  const [city, setCity] = useState('Sarajevo');
  const [country, setCountry] = useState('Bosnia and Herzegovina');
  const [prayerLoading, setPrayerLoading] = useState(false);
  const [updating, setUpdating] = useState(null);
  const [hadithOfDay, setHadithOfDay] = useState(null);
  const [showBarakahLogsModal, setShowBarakahLogsModal] = useState(false);
  const [todayActivityLogs, setTodayActivityLogs] = useState([]);
  const [barakahLogsLoading, setBarakahLogsLoading] = useState(false);
  const [deletingLogId, setDeletingLogId] = useState(null);
  const [todayPlan, setTodayPlan] = useState(() => createDefaultTodayPlan(getLocalDateString()));
  const [planDraft, setPlanDraft] = useState(() => createDefaultTodayPlan(getLocalDateString()));
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);
  const [pendingPlanEventRemovals, setPendingPlanEventRemovals] = useState([]);
  const {
    initializePriorityTimeState: initializeDraftPriorityTimeState,
    normalizePriorityFieldValue,
    registerPendingEndTimeNormalization,
  } = usePlannerPriorityTimeNormalization({ plan: planDraft, setPlan: setPlanDraft });

  const refreshQuickStats = useCallback(async () => {
    try {
      const [prayersData, summaryData] = await Promise.all([
        getPrayersToday(),
        getDailySummary().catch(() => null),
      ]);
      setPrayers(prayersData);
      setDailySummary(summaryData);
    } catch (error) {
      console.error('Error refreshing dashboard quick stats:', error);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prayersData, contentData, dhikrData, summaryData, settingsData, hadithData, planData] = await Promise.all([
          getPrayersToday(),
          getDailyContent(),
          getDhikrToday(),
          getDailySummary().catch(() => null),
          getUserPrayerSettings().catch(() => null),
          getHadithOfTheDay().catch(() => null),
          getTodayBarakahPlan().catch(() => null),
        ]);
        setPrayers(prayersData);
        setDailyContent(contentData);
        setDhikrCount(dhikrData);
        setDailySummary(summaryData);
        setHadithOfDay(hadithData);
        const normalizedPlanData = ensureTodayPlanHasGoal(planData, getLocalDateString());
        setTodayPlan(normalizedPlanData);
        setPlanDraft(normalizedPlanData);
        if (settingsData) {
          setPrayerSettings(settingsData);
          if (settingsData.default_city) setCity(settingsData.default_city);
          if (settingsData.default_country) setCountry(settingsData.default_country);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  // Instant refresh after activity logging and when user returns to tab.
  useEffect(() => {
    const handleActivityRefresh = () => {
      refreshQuickStats();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshQuickStats();
      }
    };

    window.addEventListener('bereket:activity-logged', handleActivityRefresh);
    window.addEventListener('bereket:quran-log-updated', handleActivityRefresh);
    window.addEventListener('focus', handleActivityRefresh);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('bereket:activity-logged', handleActivityRefresh);
      window.removeEventListener('bereket:quran-log-updated', handleActivityRefresh);
      window.removeEventListener('focus', handleActivityRefresh);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshQuickStats]);

  const loadTodayActivityLogs = useCallback(async () => {
    setBarakahLogsLoading(true);
    try {
      const logs = await getTodayBereketActivityLogs();
      setTodayActivityLogs(logs || []);
    } catch (error) {
      console.error('Error loading today activity logs:', error);
      toast.error(lang === 'hr' ? 'Greška pri učitavanju aktivnosti' : 'Failed to load activities');
    } finally {
      setBarakahLogsLoading(false);
    }
  }, [lang]);

  const openBarakahLogsModal = async () => {
    setShowBarakahLogsModal(true);
    await loadTodayActivityLogs();
  };

  const handleDeleteActivityLog = async (log) => {
    const confirmed = window.confirm(
      lang === 'hr'
        ? 'Obrisati ovu aktivnost iz današnje statistike?'
        : 'Delete this activity from today\'s stats?'
    );

    if (!confirmed) return;

    setDeletingLogId(log.id);
    try {
      await deleteBereketActivityLog(log.id);
      toast.success(lang === 'hr' ? 'Aktivnost obrisana' : 'Activity deleted');
      await Promise.all([refreshQuickStats(), loadTodayActivityLogs()]);
    } catch (error) {
      console.error('Error deleting activity log:', error);
      toast.error(lang === 'hr' ? 'Brisanje nije uspjelo' : 'Delete failed');
    } finally {
      setDeletingLogId(null);
    }
  };

  // Start notification scheduler on dashboard load
  useEffect(() => {
    const initNotifications = async () => {
      try {
        if ('Notification' in window && Notification.permission === 'granted') {
          const settings = await getUserNotificationSettings();
          if (settings?.push_enabled) {
            startNotificationScheduler(settings, lang);
          }
        }
      } catch (e) {
        console.error('Notification scheduler init error:', e);
      }
    };
    initNotifications();
  }, [lang]);

  // Fetch prayer times when city/country are set
  useEffect(() => {
    if (city && country) {
      fetchPrayerTimes();
    }
  }, [city, country]);

  const fetchPrayerTimes = async () => {
    setPrayerLoading(true);
    try {
      const now = new Date();
      const data = await getPrayerTimes(city, country, now.getDate(), now.getMonth() + 1, now.getFullYear());
      setPrayerData(data);
    } catch (err) {
      console.error('Error fetching prayer times:', err);
    } finally {
      setPrayerLoading(false);
    }
  };

  const adjustTime = (timeStr, minutes) => {
    if (!timeStr) return '--:--';
    const [hours, mins] = timeStr.split(':').map(Number);
    const totalMinutes = hours * 60 + mins + minutes;
    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMins = totalMinutes % 60;
    return `${String(newHours).padStart(2, '0')}:${String(newMins < 0 ? 60 + newMins : newMins).padStart(2, '0')}`;
  };

  const getCurrentPrayer = () => {
    if (!prayerData?.timings) return null;
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    const prayerOrder = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'];
    for (let i = prayerOrder.length - 1; i >= 0; i--) {
      const timeStr = prayerData.timings[prayerOrder[i]];
      if (timeStr) {
        const [hours, minutes] = timeStr.split(':').map(Number);
        if (currentTime >= hours * 60 + minutes) return prayerOrder[i];
      }
    }
    return 'isha';
  };

  const togglePrayer = async (prayerName) => {
    if (prayerName === 'sunrise') return;
    setUpdating(prayerName);
    try {
      const wasCompleted = prayers?.[prayerName];
      await updatePrayer(prayerName, !wasCompleted);
      const updatedPrayers = await getPrayersToday();
      setPrayers(updatedPrayers);

      if (!wasCompleted) {
        const activities = await getBereketActivities();
        const prayerActivityName = `Pray ${prayerName.charAt(0).toUpperCase() + prayerName.slice(1)}`;
        const activity = activities.find(a => a.name_en === prayerActivityName);
        if (activity) {
          try {
            const displayName = lang === 'hr' ? activity.name_hr : activity.name_en;
            await logBereketActivity(activity.activity_id, activity.base_points, false, displayName);
            toast.success(`${lang === 'hr' ? 'Namaz obavljen! +' : 'Prayer completed! +'}${activity.base_points} ${lang === 'hr' ? 'Bereket bodova' : 'Bereket points'}`);
          } catch (e) {
            toast.success(lang === 'hr' ? 'Namaz obavljen!' : 'Prayer completed!');
          }
        } else {
          toast.success(lang === 'hr' ? 'Namaz obavljen!' : 'Prayer completed!');
        }
      }
    } catch (error) {
      console.error('Error updating prayer:', error);
      toast.error(lang === 'hr' ? 'Greška pri ažuriranju' : 'Error updating prayer');
    } finally {
      setUpdating(null);
    }
  };

  const completedPrayers = prayers
    ? ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'].filter(p => prayers[p] === true).length
    : 0;

  const translateLevel = (level) => {
    if (!level) return t('starting_the_journey');
    if (lang === 'hr') return LEVEL_TRANSLATIONS[level] || level;
    return level;
  };

  const openTodayPlanModal = () => {
    const nextDraft = todayPlan || createDefaultTodayPlan(getLocalDateString());
    setPlanDraft(nextDraft);
    initializeDraftPriorityTimeState(nextDraft.priorities || []);
    setPendingPlanEventRemovals([]);
    setShowPlanModal(true);
  };

  const updateDraftPriority = (index, field, value) => {
    const normalizedValue = normalizePriorityFieldValue(field, value);
    setPlanDraft((prev) => {
      if (!prev) return prev;
      const priorities = [...(prev.priorities || [])];
      const current = priorities[index] || {};
      let nextPriority = { ...current, [field]: normalizedValue };

      priorities[index] = nextPriority;
      return { ...prev, priorities };
    });
  };

  const addDraftPriority = () => {
    setPlanDraft((prev) => {
      const current = prev || createDefaultTodayPlan(getLocalDateString());
      const nextPriorities = [...(current.priorities || []), createEmptyTodayPlanPriority(current.priorities?.length || 0)];
      initializeDraftPriorityTimeState(nextPriorities);
      return { ...current, priorities: nextPriorities };
    });
  };

  const removeDraftPriority = (index) => {
    setPlanDraft((prev) => {
      if (!prev) return prev;
      const priorities = [...(prev.priorities || [])];
      if (priorities.length <= 1) return prev;

      const removed = priorities[index];
      if (removed?.blocked_event_id) {
        setPendingPlanEventRemovals((current) => [...new Set([...current, removed.blocked_event_id])]);
      }

      const nextPriorities = priorities.filter((_, priorityIndex) => priorityIndex !== index);
      initializeDraftPriorityTimeState(nextPriorities);
      return { ...prev, priorities: nextPriorities };
    });
  };

  const handleSaveTodayPlan = async () => {
    if (!planDraft) return;

    const validation = validatePriorityRanges(planDraft?.priorities || []);
    if (validation.hasInvalidRanges) {
      toast.error(lang === 'hr' ? 'Ako postaviš vrijeme, ispuni oba polja i završetak mora biti nakon početka.' : 'If you set a time, fill both fields and keep the end after the start.');
      return;
    }

    if (validation.hasSequenceConflicts) {
      const confirmed = window.confirm(
        lang === 'hr'
          ? 'Otkriveno je preklapanje ili nelogičan redoslijed ciljeva. Želiš li ipak spremiti plan?'
          : 'An overlap or non-sequential goal order was detected. Do you still want to save the plan?'
      );

      if (!confirmed) {
        return;
      }
    }

    try {
      setSavingPlan(true);
      const saved = ensureTodayPlanHasGoal(await saveTodayBarakahPlan(planDraft), getLocalDateString());
      if (pendingPlanEventRemovals.length > 0) {
        await Promise.allSettled(pendingPlanEventRemovals.map((eventId) => deletePlannerEvent(eventId)));
      }
      setTodayPlan(saved);
      setPlanDraft(saved);
      setPendingPlanEventRemovals([]);
      setShowPlanModal(false);
      toast.success(lang === 'hr' ? 'Današnji plan je spremljen.' : 'Today plan saved.');
    } catch (error) {
      console.error('Save today plan error:', error);
      toast.error(lang === 'hr' ? 'Spremanje plana nije uspjelo.' : 'Failed to save today plan.');
    } finally {
      setSavingPlan(false);
    }
  };

  const currentPrayer = getCurrentPrayer();
  const dashboardHasTodayPlan = useMemo(() => hasTodayPlanContent(todayPlan), [todayPlan]);
  const dashboardVisibleGoals = useMemo(
    () => (todayPlan?.priorities || []).filter((priority) => (
      String(priority?.title || '').trim() || String(priority?.goal_niyyah || '').trim() || hasPriorityTimeRange(priority)
    )),
    [todayPlan]
  );
  const dashboardPriorityWarnings = useMemo(
    () => getPriorityWarningMap(planDraft?.priorities || []),
    [planDraft?.priorities]
  );
  const prayerOrder = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'];
  const quranSessionsToday = dailySummary?.quran?.total_sessions_today
    ?? ((dailySummary?.quran?.total_read || 0) + (dailySummary?.quran?.listened_today || 0));
  const quranListeningMinutesRaw = dailySummary?.quran?.listening_minutes_today
    ?? dailySummary?.quran?.listening_minutes
    ?? 0;
  const quranListeningMinutesToday = quranListeningMinutesRaw > 0
    ? quranListeningMinutesRaw
    : (dailySummary?.quran?.listen_sessions_today || dailySummary?.quran?.listened_today || 0);

  return (
    <Layout user={user}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 md:py-10">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-[#292524] mb-1">
                {t('welcome_back')}, {user.name}!
              </h1>
              <p className="text-[#57534E] font-body text-sm sm:text-base">
                {new Date().toLocaleDateString(undefined, {
                  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                })}
              </p>
            </div>
            <button
              onClick={() => setShowStatsModal(true)}
              data-testid="streak-stats-btn"
              className="flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-xl font-medium hover:from-amber-500 hover:to-orange-600 transition-all shadow-md"
            >
              <Flame className="w-5 h-5" />
              <span className="hidden sm:inline">{lang === 'hr' ? 'Napredak' : 'Progress'}</span>
            </button>
          </div>
        </div>

        <div className={`mb-6 bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-2xl shadow-card ${dashboardHasTodayPlan ? 'p-5' : 'p-4'}`} data-testid="dashboard-today-plan-card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-heading font-semibold text-[#292524]" data-testid="dashboard-today-plan-title">
                🎯 {lang === 'hr' ? 'Današnji plan' : 'Today plan'}
              </h2>
              <p className="text-sm text-[#57534E] mt-1" data-testid="dashboard-today-plan-niyyah-text">
                {dashboardHasTodayPlan
                  ? (todayPlan?.niyyah || (lang === 'hr' ? 'Nijet nije dodat za cijeli dan.' : 'No day-level niyyah yet.'))
                  : (lang === 'hr' ? 'Nijet i cilj još nisu postavljeni.' : 'Niyyah and goal are not set yet.')}
              </p>
            </div>
            <button
              type="button"
              onClick={openTodayPlanModal}
              className="px-3 py-2 rounded-lg text-sm font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
              data-testid="dashboard-today-plan-edit-button"
            >
              {lang === 'hr' ? 'Uredi' : 'Edit'}
            </button>
          </div>

          {dashboardHasTodayPlan ? (
            <>
              {dashboardVisibleGoals.length > 0 && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                  {dashboardVisibleGoals.map((priority, idx) => (
                    <div key={priority.id || `priority-${idx + 1}`} className="rounded-xl border border-stone-200 bg-stone-50 px-3 py-2" data-testid={`dashboard-today-plan-priority-${idx + 1}`}>
                      <p className="text-xs uppercase tracking-wide text-[#78716C]">
                        {lang === 'hr' ? `Cilj ${idx + 1}` : `Goal ${idx + 1}`}
                      </p>
                      <p className="text-sm text-[#292524] font-medium mt-1 truncate">
                        {priority?.title || (lang === 'hr' ? 'Bez naziva cilja' : 'Untitled goal')}
                      </p>
                      {priority?.goal_niyyah?.trim() && (
                        <p className="text-xs text-[#57534E] mt-1 line-clamp-2" data-testid={`dashboard-today-plan-priority-niyyah-${idx + 1}`}>
                          {priority.goal_niyyah}
                        </p>
                      )}
                      {hasPriorityTimeRange(priority) && (
                        <p className="text-xs text-[#78716C] mt-1 font-mono">
                          {priority.start_time} – {priority.end_time}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-3 flex items-center justify-between gap-3">
                <p className="text-xs text-[#78716C]" data-testid="dashboard-today-plan-avoid-text">
                  {lang === 'hr' ? 'Izbjegavati: ' : 'Avoid: '}
                  <span className="text-[#57534E]">{todayPlan?.avoid_distraction || (lang === 'hr' ? 'nije uneseno' : 'not set')}</span>
                </p>
                <Link
                  to="/planner"
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                  data-testid="dashboard-today-plan-open-planner-link"
                >
                  {lang === 'hr' ? 'Otvori timeline' : 'Open timeline'}
                </Link>
              </div>
            </>
          ) : (
            <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-dashed border-stone-200 bg-stone-50 px-3 py-2.5" data-testid="dashboard-today-plan-empty-state">
              <p className="text-xs sm:text-sm text-[#78716C]">
                {lang === 'hr' ? 'Dodaj nijet i barem jedan cilj za fokusiraniji dan.' : 'Add a niyyah and at least one goal for a more focused day.'}
              </p>
              <Link
                to="/planner"
                className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline whitespace-nowrap"
                data-testid="dashboard-today-plan-open-planner-link"
              >
                {lang === 'hr' ? 'Otvori planer' : 'Open planner'}
              </Link>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-6 mb-6 md:mb-8">
          {/* === VAKTIJA (Prayer Times) - Replaces Praćenje namaza === */}
          <div className="lg:col-span-7">
            <div className="bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-2xl p-5 sm:p-6 shadow-card h-full" data-testid="vaktija-dashboard-card">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-heading font-semibold text-[#292524]">
                      {lang === 'hr' ? 'Vaktija' : 'Prayer Times'}
                    </h2>
                    {prayerData && (
                      <div className="flex items-center gap-1.5 text-xs text-[#57534E]">
                        <MapPin className="w-3 h-3" />
                        <span>{city}</span>
                        {prayerData.hijri_date && (
                          <span className="text-primary ml-1">- {prayerData.hijri_date}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <span
                  className="text-sm font-medium text-primary bg-primary/10 px-3 py-1 rounded-full"
                  data-testid="prayer-count"
                >
                  {completedPrayers}/5
                </span>
                <Link
                  to="/prayer-times"
                  data-testid="vaktija-settings-btn"
                  className="p-2 hover:bg-stone-100 rounded-lg transition-colors text-stone-400 hover:text-primary"
                  title={lang === 'hr' ? 'Postavke vaktije' : 'Prayer time settings'}
                >
                  <Settings className="w-5 h-5" />
                </Link>
              </div>

              {prayerLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : prayerData ? (
                <div className="space-y-2.5">
                  {prayerOrder.map((prayer) => {
                    const Icon = PRAYER_ICONS[prayer] || Clock;
                    const isCurrent = prayer === currentPrayer;
                    const isSunrise = prayer === 'sunrise';
                    const isCompleted = prayers?.[prayer];
                    const rawTime = prayerData.timings?.[prayer];
                    const adjustmentKey = `${prayer}_adjustment`;
                    const adjustment = prayerSettings?.[adjustmentKey] || 0;
                    const displayTime = isSunrise ? rawTime : adjustTime(rawTime, adjustment);

                    return (
                      <div
                        key={prayer}
                        data-testid={`prayer-card-${prayer}`}
                        className={`relative flex items-center justify-between p-3 sm:p-3.5 rounded-xl transition-all border ${
                          isCurrent
                            ? 'bg-primary text-white shadow-lg border-primary'
                            : isCompleted
                            ? 'bg-green-50 border-green-200'
                            : isSunrise
                            ? 'bg-amber-50/60 border-amber-100'
                            : 'bg-stone-50/80 border-stone-100'
                        }`}
                      >
                        {isCurrent && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-accent rounded-full animate-pulse" />
                        )}
                        <div className="flex items-center gap-3">
                          <div className={`p-1.5 rounded-lg ${
                            isCurrent ? 'bg-white/20' :
                            isCompleted ? 'bg-green-100' :
                            isSunrise ? 'bg-amber-100' : 'bg-primary/10'
                          }`}>
                            <Icon className={`w-4 h-4 ${
                              isCurrent ? 'text-white' :
                              isCompleted ? 'text-green-600' :
                              isSunrise ? 'text-amber-600' : 'text-primary'
                            }`} />
                          </div>
                          <div>
                            <p className={`text-sm font-medium ${
                              isCurrent ? 'text-white/80' :
                              isCompleted ? 'text-green-700' :
                              isSunrise ? 'text-amber-700' : 'text-[#57534E]'
                            }`}>
                              {PRAYER_NAMES[lang]?.[prayer] || prayer}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <span className={`text-lg font-bold tabular-nums ${
                            isCurrent ? 'text-white' :
                            isCompleted ? 'text-green-800' :
                            isSunrise ? 'text-amber-800' : 'text-[#292524]'
                          }`}>
                            {displayTime}
                          </span>

                          {!isSunrise && (
                            <button
                              onClick={() => togglePrayer(prayer)}
                              disabled={updating === prayer}
                              data-testid={`toggle-prayer-${prayer}`}
                              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all active:scale-95 ${
                                isCurrent
                                  ? 'bg-white/20 hover:bg-white/30 text-white'
                                  : isCompleted
                                  ? 'bg-green-100 hover:bg-green-200 text-green-600'
                                  : 'bg-white border border-stone-200 hover:bg-stone-50 text-stone-400'
                              } ${updating === prayer ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {updating === prayer ? (
                                <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-current border-t-transparent" />
                              ) : isCompleted ? (
                                <CheckCircle2 className="w-5 h-5" />
                              ) : (
                                <Check className="w-4 h-4" />
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-[#A8A29E]">
                  {lang === 'hr' ? 'Učitavanje vaktije...' : 'Loading prayer times...'}
                </div>
              )}
            </div>
          </div>

          {/* === Right Column === */}
          <div className="lg:col-span-5 space-y-5 md:space-y-6">
            {/* Today's Barakah */}
            <div className="bg-gradient-to-br from-accent/10 to-primary/10 backdrop-blur-sm border border-accent/20 rounded-2xl p-5 sm:p-6 shadow-card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-accent/20 rounded-xl flex items-center justify-center">
                    <Gem className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-[#292524]">
                      {t('todays_barakah')}
                    </h3>
                    <p className="text-sm text-[#57534E]">
                      {translateLevel(dailySummary?.level)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-4xl mb-1">{dailySummary?.level_emoji || '🌾'}</div>
                </div>
              </div>

              <div className="flex items-center justify-center">
                <div className="text-center">
                  <div className="text-5xl font-heading font-bold text-accent">
                    {dailySummary?.total_points || 0}
                  </div>
                  <p className="text-sm text-[#57534E] mt-1">{t('total_points')}</p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-accent/20">
                <button
                  type="button"
                  onClick={openBarakahLogsModal}
                  data-testid="todays-barakah-view-activities-button"
                  className="w-full text-sm text-[#57534E] text-center hover:text-accent transition-colors underline-offset-4 hover:underline"
                >
                  {dailySummary?.activities_logged || 0} {t('activities_logged')} • {lang === 'hr' ? 'Prikaži / uredi' : 'View / edit'}
                </button>
              </div>
            </div>

            {/* === Brzi pregled (Quick Stats) - ENLARGED with icons === */}
            <div className="bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-2xl p-5 sm:p-6 shadow-card" data-testid="quick-stats-card">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-accent" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-[#292524]">
                  {t('quick_stats')}
                </h3>
              </div>

              <div className="space-y-3">
                {/* Namazi */}
                <div className="flex items-center justify-between p-3 bg-stone-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <BookMarked className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-sm font-medium text-[#57534E]">{t('prayers')}</span>
                  </div>
                  <span className="text-lg font-bold text-[#292524]">
                    {dailySummary?.prayers_completed || completedPrayers}/5
                  </span>
                </div>

                {/* Zapamceni ajeti (Memorized Ayahs) - replaces Dhikr */}
                <div className="flex items-center justify-between p-3 bg-stone-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                      <BookOpen className="w-4 h-4 text-violet-600" />
                    </div>
                    <span className="text-sm font-medium text-[#57534E]">
                      {lang === 'hr' ? 'Hifz ajeti' : 'Memorized'}
                    </span>
                  </div>
                  <span className="text-lg font-bold text-[#292524]">
                    {dailySummary?.quran?.memorized_ayahs || 0} {lang === 'hr' ? 'ajeta' : 'ayahs'}
                  </span>
                </div>

                {/* Kur'an (read + listened combined) */}
                <div className="flex items-center justify-between p-3 bg-stone-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Headphones className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-sm font-medium text-[#57534E]">
                      {lang === 'hr' ? "Kur'an danas" : 'Quran Today'}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-[#292524]" data-testid="quick-stats-quran-sessions-value">
                      {quranSessionsToday} {lang === 'hr' ? 'sesija' : 'sessions'}
                    </div>
                    <div className="text-xs text-[#57534E]" data-testid="quick-stats-quran-minutes-value">
                      {quranListeningMinutesToday} {lang === 'hr' ? 'min slušanja' : 'min listened'}
                    </div>
                  </div>
                </div>

                {/* Bereket */}
                <div className="flex items-center justify-between p-3 bg-stone-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
                      <Gem className="w-4 h-4 text-accent" />
                    </div>
                    <span className="text-sm font-medium text-[#57534E]">{t('barakah')}</span>
                  </div>
                  <span className="text-lg font-bold text-accent">
                    {dailySummary?.total_points || 0} {t('pts')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Daily Verse & Hadith of the Day */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {dailyContent && (
            <div
              className="bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-2xl p-6 shadow-card"
              style={{
                backgroundImage: 'linear-gradient(135deg, rgba(240,253,250,0.98) 0%, rgba(236,253,245,0.96) 45%, rgba(217,249,237,0.94) 100%)',
              }}
            >
              <div className="bg-white/95 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-heading font-semibold text-[#292524] mb-3">
                  {t('daily_verse')}
                </h3>
                <p className="text-[#292524] font-body mb-2">{dailyContent.verse.text}</p>
                <p className="text-sm text-[#57534E] font-medium">— {dailyContent.verse.reference}</p>
              </div>
            </div>
          )}

          {/* Hadith of the Day */}
          {hadithOfDay && (
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200/60 rounded-2xl p-6 shadow-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Quote className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-heading font-semibold text-[#292524]">
                    {lang === 'hr' ? 'Hadis Dana' : 'Hadith of the Day'}
                  </h3>
                  <p className="text-xs text-emerald-600">{hadithOfDay.bookName}</p>
                </div>
              </div>
              
              {/* Arabic Text */}
              {hadithOfDay.hadithArabic && (
                <p className="text-right text-lg text-[#292524] font-arabic mb-4 leading-relaxed" dir="rtl">
                  {hadithOfDay.hadithArabic}
                </p>
              )}
              
              {/* English Translation */}
              <p className="text-[#292524] font-body mb-3 leading-relaxed">
                &quot;{hadithOfDay.hadithEnglish}&quot;
              </p>
              
              {/* Narrator */}
              <p className="text-sm text-emerald-700 font-medium">
                — {hadithOfDay.englishNarrator}
              </p>
              
              {/* Topic Badge */}
              {hadithOfDay.topic && (
                <div className="mt-4 pt-3 border-t border-emerald-200">
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full font-medium">
                    #{hadithOfDay.topic}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Fallback if no hadithOfDay but dailyContent has hadith */}
          {!hadithOfDay && dailyContent && (
            <div className="bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-2xl p-6 shadow-card">
              <h3 className="text-lg font-heading font-semibold text-[#292524] mb-3">
                {t('daily_hadith')}
              </h3>
              <p className="text-[#292524] font-body mb-2">&quot;{dailyContent.hadith.text}&quot;</p>
              <p className="text-sm text-[#57534E] font-medium">— {dailyContent.hadith.reference}</p>
            </div>
          )}
        </div>
      </div>

      {showPlanModal && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setShowPlanModal(false)}
          data-testid="dashboard-today-plan-modal-overlay"
        >
          <div
            className="w-full max-w-2xl max-h-[88vh] overflow-hidden bg-white rounded-2xl shadow-2xl border border-stone-200"
            onClick={(e) => e.stopPropagation()}
            data-testid="dashboard-today-plan-modal"
          >
            <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[#292524]" data-testid="dashboard-today-plan-modal-title">
                {lang === 'hr' ? 'Uredi današnji plan' : 'Edit today plan'}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setPendingPlanEventRemovals([]);
                  setShowPlanModal(false);
                }}
                className="w-9 h-9 rounded-lg hover:bg-stone-100 flex items-center justify-center"
                data-testid="dashboard-today-plan-modal-close-button"
              >
                <X className="w-5 h-5 text-stone-500" />
              </button>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto max-h-[68vh]">
              <div>
                <label className="text-sm font-medium text-[#292524] block mb-1.5" htmlFor="today-plan-niyyah-input">
                  {lang === 'hr' ? 'Nijet' : 'Niyyah'}
                </label>
                <textarea
                  id="today-plan-niyyah-input"
                  value={planDraft?.niyyah || ''}
                  onChange={(e) => setPlanDraft((prev) => ({ ...(prev || {}), niyyah: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm text-[#292524] resize-none h-20"
                  data-testid="dashboard-today-plan-modal-niyyah-input"
                />
              </div>

              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-[#292524]">
                    {lang === 'hr' ? 'Ciljevi za danas' : 'Goals for today'}
                  </p>
                  <p className="text-xs text-[#78716C] mt-0.5">
                    {lang === 'hr' ? 'Vrijeme je opcionalno za svaki cilj.' : 'Time is optional for each goal.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addDraftPriority}
                  className="px-3 py-2 rounded-lg bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20"
                  data-testid="dashboard-today-plan-modal-add-goal-button"
                >
                  {lang === 'hr' ? '+ Dodaj cilj' : '+ Add goal'}
                </button>
              </div>

              {(planDraft?.priorities || []).map((priority, idx) => {
                const warningState = dashboardPriorityWarnings[idx];
                const hasTimeRange = hasPriorityTimeRange(priority);
                return (
                <div key={priority.id || `draft-priority-${idx + 1}`} className="border border-stone-200 rounded-xl p-3 bg-stone-50">
                  <div className="flex items-start justify-between gap-3 mb-1.5">
                    <p className="text-xs text-[#78716C] uppercase tracking-wide">
                      {lang === 'hr' ? `Cilj ${idx + 1}` : `Goal ${idx + 1}`}
                    </p>
                    {(planDraft?.priorities || []).length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeDraftPriority(idx)}
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
                        data-testid={`dashboard-today-plan-modal-remove-goal-${idx + 1}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        {lang === 'hr' ? 'Ukloni' : 'Remove'}
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    value={priority?.title || ''}
                    onChange={(e) => updateDraftPriority(idx, 'title', e.target.value)}
                    className="w-full px-3 py-2.5 border border-stone-200 rounded-lg text-sm text-[#292524]"
                    placeholder={lang === 'hr' ? 'Naziv cilja' : 'Goal title'}
                    data-testid={`dashboard-today-plan-modal-priority-title-${idx + 1}`}
                  />
                  <textarea
                    value={priority?.goal_niyyah || ''}
                    onChange={(e) => updateDraftPriority(idx, 'goal_niyyah', e.target.value)}
                    className="w-full mt-2 px-3 py-2.5 border border-stone-200 rounded-lg text-sm text-[#292524] resize-none h-16"
                    placeholder={lang === 'hr' ? 'Nijet za ovaj cilj (opcionalno)' : 'Niyyah for this goal (optional)'}
                    data-testid={`dashboard-today-plan-modal-priority-niyyah-${idx + 1}`}
                  />
                  <div className="flex gap-2 mt-2 items-center">
                    <div className="flex-1">
                      <label className="block text-xs text-[#78716C] mb-1">{lang === 'hr' ? 'Od' : 'From'}</label>
                      <input
                        type="time"
                        value={priority?.start_time || ''}
                        onChange={(e) => updateDraftPriority(idx, 'start_time', e.target.value)}
                        className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm text-[#292524]"
                        data-testid={`dashboard-today-plan-modal-priority-start-${idx + 1}`}
                      />
                    </div>
                    <span className="text-stone-400 pt-5">–</span>
                    <div className="flex-1">
                      <label className="block text-xs text-[#78716C] mb-1">{lang === 'hr' ? 'Do' : 'To'}</label>
                      <input
                        type="time"
                        value={priority?.end_time || ''}
                        onChange={(e) => {
                          updateDraftPriority(idx, 'end_time', e.target.value);
                          registerPendingEndTimeNormalization(idx);
                        }}
                        className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm text-[#292524]"
                        data-testid={`dashboard-today-plan-modal-priority-end-${idx + 1}`}
                      />
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        updateDraftPriority(idx, 'start_time', '');
                        updateDraftPriority(idx, 'end_time', '');
                      }}
                      className="text-xs text-stone-500 hover:text-stone-700"
                      data-testid={`dashboard-today-plan-modal-priority-clear-time-${idx + 1}`}
                    >
                      {lang === 'hr' ? 'Ukloni vrijeme' : 'Clear time'}
                    </button>
                    {hasTimeRange && priority?.blocked_event_id && (
                      <span className="text-[11px] text-green-700">
                        {lang === 'hr' ? 'Već blokirano u kalendaru' : 'Already blocked in calendar'}
                      </span>
                    )}
                  </div>
                  {warningState?.invalidRange && (
                    <p className="text-xs text-amber-800 mt-2" data-testid={`dashboard-today-plan-modal-priority-warning-${idx + 1}`}>
                      {lang === 'hr' ? 'Ako uneseš vrijeme, postavi oba polja i završetak mora biti nakon početka.' : 'If you set a time, fill both fields and keep the end after the start.'}
                    </p>
                  )}
                  {!warningState?.invalidRange && warningState?.sequenceConflict && (
                    <p className="text-xs text-amber-800 mt-2" data-testid={`dashboard-today-plan-modal-priority-warning-${idx + 1}`}>
                      {lang === 'hr' ? 'Ovaj cilj se vremenski preklapa ili remeti redoslijed drugih ciljeva. Možeš ipak potvrditi pri spremanju.' : 'This goal overlaps or breaks the order of other goals. You can still confirm on save.'}
                    </p>
                  )}
                </div>
              )})}

              <div>
                <label className="text-sm font-medium text-[#292524] block mb-1.5" htmlFor="today-plan-avoid-input">
                  {lang === 'hr' ? 'Izbjegavati / distrakcije' : 'Avoid / distractions'}
                </label>
                <input
                  id="today-plan-avoid-input"
                  type="text"
                  value={planDraft?.avoid_distraction || ''}
                  onChange={(e) => setPlanDraft((prev) => ({ ...(prev || {}), avoid_distraction: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm text-[#292524]"
                  data-testid="dashboard-today-plan-modal-avoid-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-[#292524] block mb-1.5" htmlFor="today-plan-notes-input">
                  {lang === 'hr' ? 'Bilješke' : 'Notes'}
                </label>
                <textarea
                  id="today-plan-notes-input"
                  value={planDraft?.notes || ''}
                  onChange={(e) => setPlanDraft((prev) => ({ ...(prev || {}), notes: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm text-[#292524] resize-none h-20"
                  data-testid="dashboard-today-plan-modal-notes-input"
                />
              </div>
            </div>

            <div className="px-5 py-4 border-t border-stone-200 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setPendingPlanEventRemovals([]);
                  setShowPlanModal(false);
                }}
                className="px-4 py-2.5 rounded-lg border border-stone-200 text-sm font-medium text-[#57534E] hover:bg-stone-50"
                data-testid="dashboard-today-plan-modal-cancel-button"
              >
                {lang === 'hr' ? 'Odustani' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={handleSaveTodayPlan}
                disabled={savingPlan}
                className="px-4 py-2.5 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                data-testid="dashboard-today-plan-modal-save-button"
              >
                {savingPlan
                  ? (lang === 'hr' ? 'Spremanje...' : 'Saving...')
                  : (lang === 'hr' ? 'Spremi plan' : 'Save plan')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBarakahLogsModal && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setShowBarakahLogsModal(false)}
          data-testid="todays-barakah-activities-modal-overlay"
        >
          <div
            className="w-full max-w-2xl max-h-[85vh] overflow-hidden bg-white rounded-2xl shadow-2xl border border-stone-200"
            onClick={(e) => e.stopPropagation()}
            data-testid="todays-barakah-activities-modal"
          >
            <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-[#292524]" data-testid="todays-barakah-activities-title">
                  {lang === 'hr' ? 'Današnje aktivnosti' : 'Today\'s Activities'}
                </h3>
                <p className="text-sm text-[#78716C]" data-testid="todays-barakah-activities-subtitle">
                  {lang === 'hr' ? 'Klikni obriši za uklanjanje iz današnjih bodova' : 'Delete entries to adjust today\'s points'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowBarakahLogsModal(false)}
                className="w-9 h-9 rounded-lg hover:bg-stone-100 flex items-center justify-center"
                data-testid="todays-barakah-activities-close-button"
              >
                <X className="w-5 h-5 text-stone-500" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto max-h-[62vh] space-y-3" data-testid="todays-barakah-activities-list">
              {barakahLogsLoading ? (
                <div className="py-10 text-center text-[#78716C]" data-testid="todays-barakah-activities-loading">
                  {lang === 'hr' ? 'Učitavanje aktivnosti...' : 'Loading activities...'}
                </div>
              ) : todayActivityLogs.length === 0 ? (
                <div className="py-10 text-center text-[#78716C]" data-testid="todays-barakah-activities-empty">
                  {lang === 'hr' ? 'Nema današnjih aktivnosti.' : 'No activities for today.'}
                </div>
              ) : (
                todayActivityLogs.map((log) => {
                  const activityName = lang === 'hr'
                    ? (log?.bereket_activities?.name_hr || log?.bereket_activities?.name_en || 'Aktivnost')
                    : (log?.bereket_activities?.name_en || log?.bereket_activities?.name_hr || 'Activity');

                  const activityTime = log?.created_at
                    ? new Date(log.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : '--:--';

                  return (
                    <div
                      key={log.id}
                      className="flex items-center justify-between gap-4 p-3 rounded-xl border border-stone-200 bg-stone-50"
                      data-testid={`todays-barakah-activity-item-${log.id}`}
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-[#292524] truncate" data-testid={`todays-barakah-activity-name-${log.id}`}>
                          {activityName}
                        </p>
                        <p className="text-xs text-[#78716C]" data-testid={`todays-barakah-activity-meta-${log.id}`}>
                          {activityTime} • +{log.points_earned} {lang === 'hr' ? 'bodova' : 'points'}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleDeleteActivityLog(log)}
                        disabled={deletingLogId === log.id}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 disabled:opacity-50"
                        data-testid={`todays-barakah-activity-delete-${log.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        {deletingLogId === log.id
                          ? (lang === 'hr' ? 'Brisanje...' : 'Deleting...')
                          : (lang === 'hr' ? 'Obriši' : 'Delete')}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Daily Streak Stats Modal */}
      <DailyStreakStats 
        isOpen={showStatsModal} 
        onClose={() => setShowStatsModal(false)} 
      />
    </Layout>
  );
}
